class CustomMeasureCurtain extends HTMLElement {
  constructor() {
    super();
    this.step = 1;
    this.totalSteps = 5;
    this.unit = 'in';
    this.activeMeasure = 'frame_width';
    this.measurements = {
      frame_width: { number: '', fraction: '0' },
      side_extension: { number: '', fraction: '0' },
      left_extension: { number: '', fraction: '0' },
      right_extension: { number: '', fraction: '0' },
      frame_to_floor: { number: '', fraction: '0' },
      mounting_height: { number: '', fraction: '0' },
      rod_width: { number: '', fraction: '0' },
      hardware_to_floor: { number: '', fraction: '0' },
      ring_bottom_floor: { number: '', fraction: '0' }
    };

    this.handleInput = this.handleInput.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.updateViewportHeight = this.updateViewportHeight.bind(this);
    this.curtainImageTransitionTimer = null;
    this.curtainImageEnterTimer = null;
  }

  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-cur-step]'));
    this.totalSteps = Number(this.dataset.totalSteps || this.steps.length || 5);
    this.unit = this.dataset.defaultUnit === 'cm' ? 'cm' : 'in';
    this.nextButton = this.querySelector('[data-cur-next]');
    this.backButton = this.querySelector('[data-cur-back]');
    this.startOverButton = this.querySelector('[data-cur-start-over]');
    this.progress = this.querySelector('[data-cur-progress]');
    this.stepCount = this.querySelector('[data-cur-step-count]');
    this.liveRegion = this.querySelector('[data-cur-live]');
    this.error = this.querySelector('[data-cur-error]');
    this.resultWidth = this.querySelector('[data-cur-result-width]');
    this.resultLength = this.querySelector('[data-cur-result-length]');
    this.sectionElement = this.closest('.custom-measure-curtain') || this;
    this.hardwareVisual = this.querySelector('.ctm-cur_rod');
    this.notInstalledVisual = this.querySelector('.ctm-cur_vtop');
    this.hardwareVisualImages = Array.from(this.querySelectorAll('[data-cur-hardware-visual]'));
    this.floorBackgrounds = Array.from(this.querySelectorAll('[data-cur-floor-background]'));
    this.ringEyeletHeightHeadings = ['pinch-pleat', 'tailor-pleat', 'triple-pinch-pleat', 'triple-tailor-pleat', 'french-pleat', 'triple-french-pleat', 'inverted-box-pleat', 'goblet', 'pinch-pleat-with-back-tab', 'flat-hooks'];
    this.rodTopHeightHeadings = ['rod-pocket', 'back-tab', 'tab-top', 'grommet'];
    this.trackBottomHeightHeadings = ['ripple-fold'];

    this.addEventListener('input', this.handleInput);
    this.addEventListener('change', this.handleChange);
    this.addEventListener('focusin', this.handleFocusIn);
    document.addEventListener('click', this.handleDocumentClick);
    window.addEventListener('resize', this.updateViewportHeight);
    window.addEventListener('orientationchange', this.updateViewportHeight);
    window.addEventListener('load', this.updateViewportHeight);
    document.addEventListener('shopify:section:load', this.updateViewportHeight);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.updateViewportHeight);
      window.visualViewport.addEventListener('scroll', this.updateViewportHeight);
    }

    if (this.nextButton) this.nextButton.addEventListener('click', () => this.goNext());
    if (this.backButton) this.backButton.addEventListener('click', () => this.goBack());
    if (this.startOverButton) this.startOverButton.addEventListener('click', () => this.startOver());

    this.querySelectorAll('[data-cur-unit]').forEach((button) => {
      button.addEventListener('click', () => this.setUnit(button.dataset.curUnit));
    });

    this.querySelectorAll('[data-cur-option]').forEach((input) => {
      input.addEventListener('keydown', (event) => this.handleOptionKeydown(event, input));
    });

    this.applyDefaultMeasurements();
    this.ensureDefaultSelectionForCurrentStep();
    this.refreshStepFlow();
    this.enhanceFractionSelects();
    this.setUnit(this.unit, { convert: false, announce: false });
    this.syncMeasurementControls();
    this.setActiveMeasure(this.activeMeasure);
    this.updateViewportHeight();
    this.showStep(1, { focus: false });
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.handleInput);
    this.removeEventListener('change', this.handleChange);
    this.removeEventListener('focusin', this.handleFocusIn);
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('resize', this.updateViewportHeight);
    window.removeEventListener('orientationchange', this.updateViewportHeight);
    window.removeEventListener('load', this.updateViewportHeight);
    document.removeEventListener('shopify:section:load', this.updateViewportHeight);
    window.clearTimeout(this.curtainImageTransitionTimer);
    window.clearTimeout(this.curtainImageEnterTimer);

    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.updateViewportHeight);
      window.visualViewport.removeEventListener('scroll', this.updateViewportHeight);
    }
  }

  enhanceFractionSelects() {
    this.querySelectorAll('[data-cur-fraction]').forEach((select, index) => {
      if (select.dataset.curEnhanced === 'true') return;

      const label = select.labels && select.labels.length ? select.labels[0].textContent.trim() : 'Fraction';
      const labelElement = select.labels && select.labels.length ? select.labels[0] : null;
      const ui = document.createElement('div');
      const button = document.createElement('button');
      const list = document.createElement('div');
      const listId = `${select.id || `cur-fraction-${index}`}-listbox`;
      const labelId = labelElement?.id || `${select.id || `cur-fraction-${index}`}-label`;

      if (labelElement && !labelElement.id) labelElement.id = labelId;

      ui.className = 'ctm-cur_frac-ui';
      ui.dataset.curFractionUi = select.dataset.curFraction;

      button.type = 'button';
      button.className = 'ctm-cur_frac-btn';
      button.setAttribute('aria-haspopup', 'listbox');
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', listId);
      if (labelElement) button.setAttribute('aria-labelledby', `${labelId} ${select.id}-value`);
      button.dataset.curFractionButton = select.dataset.curFraction;

      const buttonValue = document.createElement('span');
      buttonValue.id = `${select.id}-value`;
      button.appendChild(buttonValue);

      list.id = listId;
      list.className = 'ctm-cur_frac-list';
      list.setAttribute('role', 'listbox');
      if (labelElement) {
        list.setAttribute('aria-labelledby', labelId);
      } else {
        list.setAttribute('aria-label', label);
      }
      list.hidden = true;

      Array.from(select.options).forEach((option) => {
        const item = document.createElement('div');
        item.className = 'ctm-cur_frac-option';
        item.textContent = option.textContent;
        item.setAttribute('role', 'option');
        item.dataset.value = option.value;
        item.dataset.curFractionOption = select.dataset.curFraction;
        list.appendChild(item);
      });

      button.addEventListener('click', () => {
        if (button.disabled) return;
        this.toggleFractionSelect(ui);
      });

      button.addEventListener('keydown', (event) => this.handleFractionButtonKeydown(event, ui));
      list.addEventListener('keydown', (event) => this.handleFractionListKeydown(event, ui));
      list.addEventListener('click', (event) => {
        const item = event.target.closest('[data-cur-fraction-option]');
        if (!item) return;
        this.chooseFractionOption(ui, item.dataset.value);
      });

      ui.append(button, list);
      select.after(ui);
      select.classList.add('ctm-cur_frac--native');
      select.dataset.curEnhanced = 'true';
      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');
      this.syncFractionUi(select);
    });
  }

  handleDocumentClick(event) {
    if (event.target.closest('.ctm-cur_frac-ui')) return;
    this.closeFractionSelects();
  }

  handleFractionButtonKeydown(event, ui) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openFractionSelect(ui);
      const selected = ui.querySelector('.ctm-cur_frac-option[aria-selected="true"]');
      const first = ui.querySelector('.ctm-cur_frac-option');
      (selected || first)?.focus();
    }
  }

  handleFractionListKeydown(event, ui) {
    const options = Array.from(ui.querySelectorAll('.ctm-cur_frac-option'));
    const currentIndex = options.indexOf(document.activeElement);

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeFractionSelect(ui, { focus: true });
      return;
    }

    if (event.key === 'Tab') {
      this.closeFractionSelect(ui);
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = (currentIndex + direction + options.length) % options.length;
      options[nextIndex]?.focus();
      return;
    }

    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      options[event.key === 'Home' ? 0 : options.length - 1]?.focus();
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (document.activeElement?.dataset.value) {
        this.chooseFractionOption(ui, document.activeElement.dataset.value);
      }
    }
  }

  toggleFractionSelect(ui) {
    const isOpen = ui.classList.contains('is-open');
    if (isOpen) {
      this.closeFractionSelect(ui, { focus: true });
    } else {
      this.openFractionSelect(ui);
    }
  }

  openFractionSelect(ui) {
    this.closeFractionSelects(ui);
    ui.classList.add('is-open');
    ui.querySelector('.ctm-cur_frac-btn')?.setAttribute('aria-expanded', 'true');
    const list = ui.querySelector('.ctm-cur_frac-list');
    if (list) list.hidden = false;
    this.positionFractionSelect(ui);
  }

  closeFractionSelect(ui, { focus = false } = {}) {
    ui.classList.remove('is-open');
    ui.classList.remove('is-dropup');
    ui.querySelector('.ctm-cur_frac-btn')?.setAttribute('aria-expanded', 'false');
    ui.querySelector('.ctm-cur_frac-list').hidden = true;
    if (focus) ui.querySelector('.ctm-cur_frac-btn')?.focus();
  }

  positionFractionSelect(ui) {
    const list = ui.querySelector('.ctm-cur_frac-list');
    if (!list || list.hidden) return;

    ui.classList.remove('is-dropup');

    const listRect = list.getBoundingClientRect();
    const buttonRect = ui.querySelector('.ctm-cur_frac-btn')?.getBoundingClientRect();
    const footerRect = this.querySelector('.ctm-cur_ftr')?.getBoundingClientRect();
    const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    const safeBottom = footerRect && footerRect.top < viewportHeight ? footerRect.top : viewportHeight;
    const gap = 12;

    if (buttonRect && listRect.bottom + gap > safeBottom && buttonRect.top > listRect.height + gap) {
      ui.classList.add('is-dropup');
    }
  }

  closeFractionSelects(except = null) {
    this.querySelectorAll('.ctm-cur_frac-ui.is-open').forEach((ui) => {
      if (ui !== except) this.closeFractionSelect(ui);
    });
  }

  chooseFractionOption(ui, value) {
    const key = ui.dataset.curFractionUi;
    const select = this.querySelector(`[data-cur-fraction="${key}"]`);
    if (!select) return;

    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    this.syncFractionUi(select);
    this.closeFractionSelect(ui, { focus: true });
  }

  syncFractionUi(select) {
    const ui = select.nextElementSibling?.matches('.ctm-cur_frac-ui') ? select.nextElementSibling : null;
    if (!ui) return;

    const selectedOption = select.selectedOptions[0] || select.options[0];
    const button = ui.querySelector('.ctm-cur_frac-btn');
    const label = select.labels && select.labels.length ? select.labels[0].textContent.trim() : 'Fraction';

    if (button && selectedOption) {
      const valueElement = button.querySelector('span');
      if (valueElement) valueElement.textContent = selectedOption.textContent;
      button.disabled = select.disabled || select.hidden;
      if (!button.hasAttribute('aria-labelledby')) {
        button.setAttribute('aria-label', `${label}: ${selectedOption.textContent}`);
      }
    }

    ui.hidden = select.hidden;
    ui.querySelectorAll('.ctm-cur_frac-option').forEach((item) => {
      const isSelected = item.dataset.value === select.value;
      item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      item.tabIndex = isSelected ? 0 : -1;
    });
  }

  updateViewportHeight() {
    const target = this.sectionElement || this;
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 989px)').matches;
    this.querySelectorAll('.ctm-cur_frac-ui.is-open').forEach((ui) => this.positionFractionSelect(ui));

    if (isMobile) {
      target.style.removeProperty('--ctm-cur_vh');
      this.updateBackgroundFloorHeight(target, 0);
      return;
    }

    const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    const sectionTop = Math.max(0, target.getBoundingClientRect().top);
    const topOffset = Math.max(sectionTop, this.getTopOverlayHeight(target));
    const availableHeight = Math.max(520, Math.floor(viewportHeight - topOffset));
    target.style.setProperty('--ctm-cur_vh', `${availableHeight}px`);
    this.updateBackgroundFloorHeight(target, availableHeight);
  }

  updateBackgroundFloorHeight(target, availableHeight) {
    const visual = this.querySelector('.ctm-cur_vis');
    const wall = this.querySelector('.ctm-cur_bg--wall');
    const floor = this.querySelector('.ctm-cur_bg--floor.is-active') || this.querySelector('.ctm-cur_bg--floor');

    if (!target || !visual || !wall || !floor) return;

    const wallStyle = getComputedStyle(wall);
    const floorStyle = getComputedStyle(floor);
    const wallImageWidth = parseFloat(wallStyle.getPropertyValue('--ctm-cur_wall-image-width'));
    const wallImageHeight = parseFloat(wallStyle.getPropertyValue('--ctm-cur_wall-image-height'));
    const floorImageWidth = parseFloat(floorStyle.getPropertyValue('--ctm-cur_floor-image-width'));
    const floorImageHeight = parseFloat(floorStyle.getPropertyValue('--ctm-cur_floor-image-height'));

    if (!wallImageWidth || !wallImageHeight) return;

    const visualRect = visual.getBoundingClientRect();
    const visualWidth = visualRect.width;
    const visualHeight = visualRect.height || availableHeight;
    const isFloorHidden = floorStyle.display === 'none';

    if (isFloorHidden) {
      const scale = Math.max(visualWidth / wallImageWidth, visualHeight / wallImageHeight);
      const renderedWidth = Math.ceil(wallImageWidth * scale);

      target.style.setProperty('--ctm-cur_bg-rendered-width', `${renderedWidth}px`);
      target.style.setProperty('--ctm-cur_wall-rendered-h', `${visualHeight}px`);
      target.style.setProperty('--ctm-cur_flr-rendered-h', '0px');
      return;
    }

    if (!floorImageWidth || !floorImageHeight) return;

    const totalImageHeight = wallImageHeight + floorImageHeight;
    const backgroundWidth = Math.max(wallImageWidth, floorImageWidth);
    const widthScale = visualWidth / backgroundWidth;
    const heightScale = visualHeight / totalImageHeight;
    const scale = Math.max(widthScale, heightScale);
    const renderedWidth = Math.ceil(backgroundWidth * scale);
    const wallHeight = Math.ceil(wallImageHeight * scale);
    const floorHeight = Math.ceil(floorImageHeight * scale);

    target.style.setProperty('--ctm-cur_bg-rendered-width', `${renderedWidth}px`);
    target.style.setProperty('--ctm-cur_wall-rendered-h', `${wallHeight}px`);
    target.style.setProperty('--ctm-cur_flr-rendered-h', `${floorHeight}px`);
  }

  getTopOverlayHeight(target) {
    const selectors = [
      '.shopify-section-group-header-group',
      '.header-section',
      '#shopify-section-header',
      'sticky-header',
      'header[is="sticky-header"]'
    ];

    return selectors.reduce((height, selector) => {
      const element = document.querySelector(selector);
      if (!element || element.contains(target)) return height;

      const style = window.getComputedStyle(element);
      if (style.position !== 'fixed' && style.position !== 'sticky') return height;

      const rect = element.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top > 1) return height;

      return Math.max(height, rect.bottom);
    }, 0);
  }

  handleInput(event) {
    const input = event.target.closest('[data-cur-measure-input]');
    if (!input) return;

    const key = input.dataset.curMeasureInput;
    if (!this.measurements[key]) return;

    const value = this.sanitizeMeasurementInput(input, this.unit);
    this.measurements[key].number = value;
    this.rememberMeasurementSource(key);
    this.setActiveMeasure(key);
    this.updateResults();
    this.updateValidity();
  }

  handleChange(event) {
    const option = event.target.closest('[data-cur-option]');
    if (option) {
      this.refreshStepFlow();
      this.updateVisualState();
      this.updateMeasurementMode();
      this.updateResults();
      this.updateValidity();
      this.clearError();
      return;
    }

    const fraction = event.target.closest('[data-cur-fraction]');
    if (!fraction) return;

    const key = fraction.dataset.curFraction;
    if (!this.measurements[key]) return;

    this.measurements[key].fraction = fraction.value;
    this.rememberMeasurementSource(key);
    this.setActiveMeasure(key);
    this.syncFractionUi(fraction);
    this.updateResults();
    this.updateValidity();
  }

  handleFocusIn(event) {
    const row = event.target.closest('[data-cur-measurement]');
    if (!row) return;

    this.setActiveMeasure(row.dataset.curMeasurement);
    this.announce(`${this.getMeasureLabel(row)} measurement selected.`);
  }

  handleOptionKeydown(event, input) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  goNext() {
    const defaultSelectionChanged = this.ensureDefaultSelectionForCurrentStep();
    if (defaultSelectionChanged) {
      this.refreshStepFlow();
      this.updateVisualState();
      this.updateMeasurementMode();
      this.updateResults();
      this.updateValidity();
    }

    if (!this.isStepValid(this.step)) {
      this.showError(this.dataset.requiredMessage || 'Complete this step to continue.');
      return;
    }

    this.clearError();
    const nextStep = this.getAdjacentStep(1);
    if (!nextStep || this.step >= this.totalSteps) return;
    this.showStep(nextStep);
  }

  goBack() {
    if (this.step <= 1) return;
    this.clearError();
    const previousStep = this.getAdjacentStep(-1);
    if (previousStep) this.showStep(previousStep);
  }

  showStep(step, { focus = true } = {}) {
    this.refreshStepFlow();
    const requestedStep = Number(step);
    const fallbackStep = this.visibleStepNumbers[0] || 1;
    this.step = this.visibleStepNumbers.includes(requestedStep)
      ? requestedStep
      : this.getClosestVisibleStep(requestedStep) || fallbackStep;
    this.dataset.step = String(this.step);

    this.steps.forEach((stepElement) => {
      const isActive = Number(stepElement.dataset.curStep) === this.step;
      stepElement.hidden = !isActive;
      stepElement.toggleAttribute('aria-hidden', !isActive);
    });

    const defaultSelectionChanged = this.ensureDefaultSelectionForCurrentStep();
    if (defaultSelectionChanged) this.refreshStepFlow();
    this.ensureDefaultFinishSelection();

    if (this.nextButton) {
      this.nextButton.hidden = this.getDisplayStepIndex() === this.totalSteps;
      this.nextButton.textContent = this.nextButton.dataset.defaultLabel || 'Continue';
    }

    this.updateProgress();
    this.updateBackButton();
    this.updateVisualState();
    this.updateFinishDescriptions();
    this.updateMeasurementMode();
    this.updateDynamicMeasurementText();
    this.updateResults();
    this.updateValidity();
    this.updateViewportHeight();
    this.announce(`Step ${this.step} of ${this.totalSteps}`);

    if (focus) this.focusCurrentStep();
  }

  updateProgress() {
    const displayStep = this.getDisplayStepIndex();
    const percent = `${(displayStep / this.totalSteps) * 100}%`;

    if (this.progress) {
      this.progress.style.setProperty('--ctm-cur_progress', percent);
      this.progress.setAttribute('aria-valuenow', String(displayStep));
      this.progress.setAttribute('aria-valuemax', String(this.totalSteps));
    }

    if (this.stepCount) this.stepCount.textContent = `Step ${displayStep} of ${this.totalSteps}`;
  }

  updateBackButton() {
    if (!this.backButton) return;
    this.backButton.hidden = this.step <= 1;
  }

  updateValidity() {
    if (!this.nextButton || this.getDisplayStepIndex() === this.totalSteps) return;
    this.nextButton.disabled = !this.isStepValid(this.step);
  }

  isStepValid(step) {
    if (step === 1) return Boolean(this.getSelectedOption('hardware'));
    if (step === 2) return Boolean(this.getSelectedOption('heading'));
    if (step === 3) return this.areMeasurementsComplete();
    if (step === 4) return Boolean(this.getSelectedOption('panel'));
    if (step === 5) return !this.needsFullnessStep() || Boolean(this.getSelectedOption('fullness'));
    if (step === 6) return Boolean(this.getSelectedOption('finish'));
    return true;
  }

  refreshStepFlow() {
    const shouldShowFullness = this.needsFullnessStep();
    if (!shouldShowFullness) {
      this.querySelectorAll('[data-cur-option="fullness"]').forEach((option) => {
        option.checked = false;
      });
    }
    this.updateFullnessOptions();
    this.visibleStepNumbers = this.steps
      .filter((stepElement) => stepElement.dataset.curConditional !== 'fullness' || shouldShowFullness)
      .map((stepElement) => Number(stepElement.dataset.curStep))
      .filter(Boolean);
    this.totalSteps = this.visibleStepNumbers.length || this.steps.length || 5;
    this.dataset.totalSteps = String(this.totalSteps);
  }

  getAdjacentStep(direction) {
    const currentIndex = this.visibleStepNumbers.indexOf(this.step);
    if (currentIndex === -1) return this.getClosestVisibleStep(this.step);
    return this.visibleStepNumbers[currentIndex + direction] || null;
  }

  getClosestVisibleStep(step) {
    if (!this.visibleStepNumbers.length) return null;
    if (this.visibleStepNumbers.includes(step)) return step;
    return this.visibleStepNumbers.find((visibleStep) => visibleStep > step) || this.visibleStepNumbers[this.visibleStepNumbers.length - 1];
  }

  getDisplayStepIndex() {
    if (!this.visibleStepNumbers || !this.visibleStepNumbers.length) return this.step || 1;

    const currentIndex = this.visibleStepNumbers.indexOf(this.step);
    return currentIndex === -1 ? 1 : currentIndex + 1;
  }

  areMeasurementsComplete() {
    return this.getActiveMeasurementKeys().every((key) => {
      const value = this.measurements[key];
      const number = value ? value.number : '';
      if (number === '') return false;
      return Number(number) >= 0 && (key === 'side_extension' || key === 'left_extension' || key === 'right_extension' || Number(number) > 0);
    });
  }

  setUnit(unit, { convert = true, announce = true } = {}) {
    const nextUnit = unit === 'cm' ? 'cm' : 'in';
    const previousUnit = this.unit;
    if (nextUnit === previousUnit && convert) return;

    if (convert) {
      Object.keys(this.measurements).forEach((key) => {
        const measurement = this.measurements[key];
        if (measurement.number === '') return;

        if (measurement.sourceUnit === nextUnit) {
          measurement.number = measurement.sourceNumber;
          measurement.fraction = measurement.sourceFraction;
          return;
        }

        const decimalInches = this.getValueInInches(key, previousUnit);
        if (nextUnit === 'cm') {
          measurement.number = this.formatNumber(decimalInches * 2.54, 1);
          measurement.fraction = '0';
        } else {
          const wholeAndFraction = this.toWholeAndFraction(decimalInches);
          measurement.number = wholeAndFraction.number;
          measurement.fraction = wholeAndFraction.fraction;
        }

        measurement.number = this.sanitizeMeasurementValue(measurement.number, nextUnit);
      });
      this.syncMeasurementControls();
    }

    this.unit = nextUnit;

    this.querySelectorAll('[data-cur-unit]').forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.curUnit === nextUnit ? 'true' : 'false');
    });

    this.querySelectorAll('[data-cur-fraction]').forEach((select) => {
      select.hidden = nextUnit === 'cm';
      select.disabled = nextUnit === 'cm';
      this.syncFractionUi(select);
    });

    this.querySelectorAll('[data-cur-unit-suffix]').forEach((suffix) => {
      suffix.textContent = nextUnit === 'cm' ? 'cm' : 'Inches';
    });

    this.querySelectorAll('[data-cur-measure-input]').forEach((input) => {
      input.step = nextUnit === 'cm' ? '0.1' : '1';
      input.inputMode = nextUnit === 'cm' ? 'decimal' : 'numeric';
      input.maxLength = nextUnit === 'cm' ? 5 : 3;
      input.pattern = nextUnit === 'cm' ? '^\\d{0,3}(\\.\\d?)?$' : '^\\d{0,3}$';
      input.value = this.sanitizeMeasurementValue(input.value, nextUnit);
    });

    this.updateFinishDescriptions();
    this.updateMeasurementMode();
    this.updateResults();
    this.updateValidity();
    if (announce) this.announce(nextUnit === 'cm' ? 'Unit changed to centimeters.' : 'Unit changed to inches.');
  }

  rememberMeasurementSource(key) {
    const measurement = this.measurements[key];
    if (!measurement) return;

    measurement.sourceUnit = this.unit;
    measurement.sourceNumber = measurement.number;
    measurement.sourceFraction = this.unit === 'cm' ? '0' : measurement.fraction;
  }

  sanitizeMeasurementInput(input, unit) {
    const value = this.sanitizeMeasurementValue(input.value, unit);
    if (input.value !== value) input.value = value;
    return value;
  }

  sanitizeMeasurementValue(value, unit) {
    const normalized = String(value || '').replace(',', '.').replace(/[^\d.]/g, '');
    if (!normalized) return '';

    const parts = normalized.split('.');
    const whole = parts.shift().slice(0, 3);

    if (unit !== 'cm') return whole;
    if (!parts.length) return whole;
    if (!whole) return '';

    const decimal = parts.join('').slice(0, 1);
    return `${whole}.${decimal}`;
  }

  applyDefaultMeasurements() {
    this.querySelectorAll('[data-cur-measure-input][data-cur-default-number]').forEach((input) => {
      const key = input.dataset.curMeasureInput;
      const measurement = this.measurements[key];
      if (!measurement) return;

      const value = this.sanitizeMeasurementValue(input.dataset.curDefaultNumber, this.unit);
      measurement.number = value;
      measurement.fraction = '0';
      measurement.sourceUnit = this.unit;
      measurement.sourceNumber = value;
      measurement.sourceFraction = '0';
    });
  }

  syncMeasurementControls() {
    Object.keys(this.measurements).forEach((key) => {
      const value = this.measurements[key];
      this.querySelectorAll(`[data-cur-measure-input="${key}"]`).forEach((input) => {
        input.value = value.number;
      });
      this.querySelectorAll(`[data-cur-fraction="${key}"]`).forEach((select) => {
        select.value = value.fraction;
        this.syncFractionUi(select);
      });
    });
  }

  updateFinishDescriptions() {
    const configurableDescriptions = this.querySelectorAll('[data-cur-finish-description]');
    if (configurableDescriptions.length) {
      configurableDescriptions.forEach((description) => {
        const nextText = this.unit === 'cm' ? description.dataset.cmDescription : description.dataset.inDescription;
        description.textContent = nextText || '';
      });
      return;
    }

    const fallbackDescriptions = {
      hover: {
        in: '-1 inch clearance for clean look',
        cm: '-2.54 cm clearance for clean look'
      },
      float: {
        in: '-1 inch clearance for clean look',
        cm: '-2.54 cm clearance for clean look'
      },
      kiss: {
        in: 'Just touching floor for tailored finish (0 inch)',
        cm: 'Just touching floor for tailored finish (0 cm)'
      },
      puddle: {
        in: '+1 inch pooling for better light seal',
        cm: '+2.54 cm pooling for better light seal'
      }
    };

    this.querySelectorAll('[data-cur-option="finish"]').forEach((input) => {
      const card = input.closest('.ctm-cur_card');
      const description = card ? card.querySelector('.ctm-cur_card-cpy') : null;
      const descriptions = fallbackDescriptions[input.value];
      if (!description || !descriptions) return;
      const nextText = this.unit === 'cm' ? descriptions.cm : descriptions.in;
      description.textContent = nextText || '';
    });
  }

  getValueInInches(key, unit = this.unit) {
    const value = this.measurements[key];
    if (!value) return 0;

    const number = Number(value.number || 0);
    if (unit === 'cm') return number / 2.54;

    return number + this.fractionToDecimal(value.fraction);
  }

  setActiveMeasure(key) {
    if (!key || !this.measurements[key]) return;

    this.activeMeasure = key;
    this.dataset.activeMeasure = key;

    this.querySelectorAll('[data-cur-measurement]').forEach((row) => {
      row.classList.toggle('is-active', row.dataset.curMeasurement === key);
    });
  }

  updateVisualState() {
    const hardware = this.getSelectedOption('hardware');
    const panel = this.getSelectedOption('panel');
    const finish = this.getSelectedOption('finish');
    const heading = this.getSelectedOption('heading');
    const fullness = this.getSelectedOption('fullness');
    const hardwareMode = this.getHardwareMode();
    const hasHardwareSelection = Boolean(hardware);
    this.dataset.hardware = hasHardwareSelection ? hardwareMode : '';
    this.classList.toggle('is-hardware-installed', hasHardwareSelection && hardwareMode === 'installed');
    this.classList.toggle('is-hardware-not-installed', hasHardwareSelection && hardwareMode === 'not-installed');
    if (this.sectionElement) {
      this.sectionElement.classList.toggle('is-hardware-installed', hasHardwareSelection && hardwareMode === 'installed');
      this.sectionElement.classList.toggle('is-hardware-not-installed', hasHardwareSelection && hardwareMode === 'not-installed');
    }
    this.dataset.headingStyle = heading ? heading.value : '';
    this.dataset.installedHeightVisual = this.getInstalledHeightVisualType();
    this.dataset.panelConfig = panel ? panel.value : '';
    this.dataset.fullness = fullness ? fullness.value : '';
    this.dataset.finishedLook = this.step === 4 ? 'kiss' : finish ? finish.value : '';
    this.updateFloorBackground(panel);
    this.updateCurtainImage(panel);
    this.updateHardwareVisual(hasHardwareSelection ? hardwareMode : '');
  }

  updateFloorBackground(panel) {
    if (this.step > 4) return;

    const panelValue = panel ? this.normalizeOptionValue(panel.value) : 'single';
    const floorMode = panelValue === 'pair' ? 'pair' : 'single';

    this.floorBackgrounds?.forEach((background) => {
      background.classList.toggle('is-active', background.dataset.curFloorBackground === floorMode);
    });

    this.updateViewportHeight();
  }

  updateCurtainImage(panel) {
    const resultFilenames = this.getCurtainImageFilenamesForCurrentStep();
    const resultFilename = resultFilenames[0] || '';
    const resultImage = this.getResultCurtainImageUrl(resultFilename);
    const hasCandidateImage = Boolean(resultImage);
    const target = this.sectionElement || this;

    this.setCurtainImageDebug({
      filename: resultFilename,
      url: resultImage,
      status: hasCandidateImage ? 'loading' : 'missing-selection'
    });

    if (hasCandidateImage) {
      this.preloadResultCurtainImage(resultImage, resultFilename, resultFilenames);
    } else {
      this.classList.toggle('has-panel-curtain-image', false);
      target.classList.toggle('has-panel-curtain-image', false);
      target.style.removeProperty('--ctm-cur_panel-image');
      delete target.dataset.curResultImage;
      this.setCurtainImageLayer('current', '');
      this.setCurtainImageLayer('next', '');
    }
  }

  setCurtainImage(image, filename = '') {
    const target = this.sectionElement || this;
    const currentFilename = target.dataset.curResultImage || '';

    if ((this.step === 4 || this.step === 5 || this.step === 6) && filename && currentFilename && currentFilename !== filename) {
      this.transitionCurtainImage(image, filename);
      return;
    }

    if ((this.step === 4 || this.step === 5 || this.step === 6) && filename && !currentFilename) {
      this.enterCurtainImage(image, filename);
      return;
    }

    window.clearTimeout(this.curtainImageTransitionTimer);
    window.clearTimeout(this.curtainImageEnterTimer);
    this.classList.remove('is-curtain-image-changing');
    this.classList.remove('is-curtain-image-entering');
    this.classList.remove('is-curtain-image-ready');
    this.classList.remove('is-curtain-image-settling');
    target.classList.remove('is-curtain-image-changing');
    target.classList.remove('is-curtain-image-entering');
    target.classList.remove('is-curtain-image-ready');
    target.classList.remove('is-curtain-image-settling');
    target.style.setProperty('--ctm-cur_panel-image', `url("${image}")`);

    this.setCurtainImageLayer('current', image);
    this.setCurtainImageLayer('next', '');

    if (filename) target.dataset.curResultImage = filename;
    else delete target.dataset.curResultImage;
  }

  transitionCurtainImage(image, filename) {
    const target = this.sectionElement || this;

    window.clearTimeout(this.curtainImageTransitionTimer);
    window.clearTimeout(this.curtainImageEnterTimer);
    this.classList.remove('is-curtain-image-entering');
    this.classList.remove('is-curtain-image-settling');
    target.classList.remove('is-curtain-image-entering');
    target.classList.remove('is-curtain-image-settling');
    this.setCurtainImageLayer('next', image);
    this.classList.add('is-curtain-image-ready');
    target.classList.add('is-curtain-image-ready');

    requestAnimationFrame(() => {
      this.classList.add('is-curtain-image-changing');
      target.classList.add('is-curtain-image-changing');
    });

    this.curtainImageTransitionTimer = window.setTimeout(() => {
      target.style.setProperty('--ctm-cur_panel-image', `url("${image}")`);
      target.dataset.curResultImage = filename;
      this.setCurtainImageLayer('current', image);
      this.classList.add('is-curtain-image-settling');
      target.classList.add('is-curtain-image-settling');
      this.setCurtainImageLayer('next', '');

      requestAnimationFrame(() => {
        this.classList.remove('is-curtain-image-changing');
        this.classList.remove('is-curtain-image-ready');
        target.classList.remove('is-curtain-image-changing');
        target.classList.remove('is-curtain-image-ready');

        requestAnimationFrame(() => {
          this.classList.remove('is-curtain-image-settling');
          target.classList.remove('is-curtain-image-settling');
        });
      });
    }, 440);
  }

  enterCurtainImage(image, filename) {
    const target = this.sectionElement || this;

    window.clearTimeout(this.curtainImageTransitionTimer);
    window.clearTimeout(this.curtainImageEnterTimer);
    this.classList.remove('is-curtain-image-changing');
    this.classList.remove('is-curtain-image-settling');
    target.classList.remove('is-curtain-image-changing');
    target.classList.remove('is-curtain-image-settling');
    this.classList.add('is-curtain-image-entering');
    target.classList.add('is-curtain-image-entering');

    target.style.setProperty('--ctm-cur_panel-image', `url("${image}")`);
    this.setCurtainImageLayer('current', image);
    this.setCurtainImageLayer('next', '');
    target.dataset.curResultImage = filename;

    this.curtainImageEnterTimer = window.setTimeout(() => {
      requestAnimationFrame(() => {
        this.classList.remove('is-curtain-image-entering');
        target.classList.remove('is-curtain-image-entering');
      });
    }, 40);
  }

  setCurtainImageLayer(layer, image) {
    const layerElement = this.querySelector(`.ctm-cur_panel-layer--${layer}`);
    if (!layerElement) return;

    if (image) {
      layerElement.style.setProperty('--ctm-cur_layer-image', `url("${image}")`);
      layerElement.hidden = false;
    } else {
      layerElement.style.removeProperty('--ctm-cur_layer-image');
      layerElement.hidden = true;
    }
  }

  preloadResultCurtainImage(image, filename, filenames = []) {
    if (!image) return;

    const loader = new Image();
    const requestKey = filenames.join('|') || filename;
    this.resultCurtainImageRequest = requestKey;

    loader.onload = () => {
      if (this.resultCurtainImageRequest !== requestKey) return;
      const target = this.sectionElement || this;
      if (target.dataset.curResultImage === filename) {
        this.setCurtainImageDebug({ filename, url: image, status: 'loaded' });
        return;
      }
      this.classList.toggle('has-panel-curtain-image', true);
      target.classList.toggle('has-panel-curtain-image', true);
      this.setCurtainImage(image, filename);
      this.setCurtainImageDebug({ filename, url: image, status: 'loaded' });
    };

    loader.onerror = () => {
      if (this.resultCurtainImageRequest !== requestKey) return;
      if (!this.getCurtainImageFilenamesForCurrentStep().includes(filename)) return;

      const nextFilename = filenames.find((candidate) => candidate !== filename);
      const nextImage = this.getResultCurtainImageUrl(nextFilename);
      if (nextImage) {
        this.setCurtainImageDebug({ filename: nextFilename, url: nextImage, status: 'loading' });
        this.preloadResultCurtainImage(nextImage, nextFilename, filenames.filter((candidate) => candidate !== filename));
        return;
      }

      this.setCurtainImageDebug({ filename, url: image, status: 'not-found' });
      if (window.console) {
        console.warn(`Curtain result image not found: ${filenames.join(', ') || filename}`, image);
      }
    };

    loader.src = image;
  }

  setCurtainImageDebug({ filename = '', url = '', status = '' } = {}) {
    const target = this.sectionElement || this;
    target.dataset.curResultImageFilename = filename;
    target.dataset.curResultImageUrl = url;
    target.dataset.curResultImageStatus = status;
    this.dataset.curResultImageFilename = filename;
    this.dataset.curResultImageUrl = url;
    this.dataset.curResultImageStatus = status;
  }

  getResultCurtainImageUrl(filename = this.getResultCurtainImageFilename()) {
    if (!filename) return '';

    const template = this.dataset.curImageUrlTemplate;
    if (!template) return '';

    return template.replace('curtain_IMAGE_NAME.png', filename);
  }

  getResultCurtainImageFilename() {
    return this.getResultCurtainImageFilenames()[0] || '';
  }

  getCurtainImageFilenamesForCurrentStep() {
    if (this.step === 4) return this.getPanelPreviewCurtainImageFilenames();
    if (this.step === 5) return this.getFullnessPreviewCurtainImageFilenames();
    return this.getResultCurtainImageFilenames();
  }

  getResultCurtainImageFilenames() {
    const imageCodes = this.getResultCurtainImageCodes();
    const requiredCodes = [imageCodes.heading, imageCodes.panel, imageCodes.finish];
    if (this.needsFullnessStep()) requiredCodes.push(imageCodes.fullness);
    this.setCurtainImageCodesDebug(imageCodes);

    if (requiredCodes.some((code) => !code)) return [];

    const codes = [imageCodes.heading, imageCodes.panel, imageCodes.fullness, imageCodes.finish].filter(Boolean);
    return this.getCurtainImageFilenames(codes);
  }

  getPanelPreviewCurtainImageFilenames() {
    const imageCodes = this.getPanelPreviewCurtainImageCodes();
    const requiredCodes = [imageCodes.heading, imageCodes.panel, imageCodes.finish];
    this.setCurtainImageCodesDebug(imageCodes);

    if (requiredCodes.some((code) => !code)) return [];

    const filenameGroups = [
      [imageCodes.heading, imageCodes.panel, imageCodes.fullness, imageCodes.finish].filter(Boolean),
      [imageCodes.heading, imageCodes.panel, imageCodes.finish].filter(Boolean)
    ];

    return filenameGroups.flatMap((codes) => this.getCurtainImageFilenames(codes)).filter((filename, index, filenames) => {
      return filenames.indexOf(filename) === index;
    });
  }

  getFullnessPreviewCurtainImageFilenames() {
    const imageCodes = this.getFullnessPreviewCurtainImageCodes();
    const requiredCodes = [imageCodes.heading, imageCodes.panel, imageCodes.finish];
    this.setCurtainImageCodesDebug(imageCodes);

    if (requiredCodes.some((code) => !code)) return [];

    const filenameGroups = [
      [imageCodes.heading, imageCodes.panel, imageCodes.fullness, imageCodes.finish].filter(Boolean),
      [imageCodes.heading, imageCodes.panel, imageCodes.finish].filter(Boolean)
    ];

    return filenameGroups.flatMap((codes) => this.getCurtainImageFilenames(codes)).filter((filename, index, filenames) => {
      return filenames.indexOf(filename) === index;
    });
  }

  getCurtainImageFilenames(codes) {
    if (!codes.length) return [];
    const extension = this.normalizeImageExtension(this.dataset.curImageExtension || 'png');
    const baseFilename = `curtain_${codes.join('_')}`;
    return [`${baseFilename}.${extension}`];
  }

  getResultCurtainImageCodes() {
    return {
      heading: this.getSelectedImageCode('heading'),
      panel: this.getSelectedImageCode('panel'),
      fullness: this.getFullnessImageCode(),
      finish: this.getSelectedImageCode('finish')
    };
  }

  getPanelPreviewCurtainImageCodes() {
    return {
      heading: this.getSelectedImageCode('heading'),
      panel: this.getPanelImageCode(),
      fullness: this.getDefaultPreviewFullnessImageCode(),
      finish: 'kiss'
    };
  }

  getFullnessPreviewCurtainImageCodes() {
    return {
      heading: this.getSelectedImageCode('heading'),
      panel: this.getPanelImageCode(),
      fullness: this.getSelectedImageCode('fullness'),
      finish: 'kiss'
    };
  }

  getDefaultPreviewFullnessImageCode() {
    if (!this.needsFullnessStep()) return '';

    const fullnessOption = Array.from(this.querySelectorAll('[data-cur-option="fullness"]')).find((option) => {
      return !option.disabled && Math.abs(Number(option.dataset.curFullnessMultiplier) - 1.5) < 0.01;
    });

    return fullnessOption ? this.normalizeImageCode(fullnessOption.dataset.curImageCode || fullnessOption.value) : 'fullness-1-5x';
  }

  setCurtainImageCodesDebug(codes) {
    const target = this.sectionElement || this;
    const serializedCodes = JSON.stringify(codes);
    target.dataset.curResultImageCodes = serializedCodes;
    this.dataset.curResultImageCodes = serializedCodes;
  }

  getSelectedImageCode(type) {
    const option = this.getSelectedOption(type);
    if (!option) return '';

    return this.normalizeImageCode(option.dataset.curImageCode || option.value);
  }

  getPanelImageCode() {
    const panel = this.getSelectedOption('panel');
    if (!panel) return '';

    const code = this.normalizeImageCode(panel.dataset.curImageCode || panel.value);
    if (code.includes('single')) return 'single';
    if (code.includes('pair')) return 'pair';
    return code;
  }

  getFullnessImageCode() {
    if (!this.needsFullnessStep()) {
      return '';
    }

    return this.getSelectedImageCode('fullness');
  }

  normalizeImageCode(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  normalizeImageExtension(value) {
    const extension = String(value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    return ['jpg', 'png', 'webp'].includes(extension) ? extension : 'png';
  }

  updateHardwareVisual(mode) {
    this.hardwareVisualImages?.forEach((image) => {
      const isActive = mode && image.dataset.curHardwareVisual === mode;
      image.hidden = !isActive;
    });

    if (this.hardwareVisual) {
      this.hardwareVisual.classList.toggle('is-installed', mode === 'installed');
      this.hardwareVisual.classList.toggle('is-not-installed', false);
    }

    if (this.notInstalledVisual) {
      this.notInstalledVisual.classList.toggle('is-installed', false);
      this.notInstalledVisual.classList.toggle('is-not-installed', mode === 'not-installed');
    }
  }

  updateMeasurementMode() {
    const mode = this.getHardwareMode();

    this.querySelectorAll('[data-cur-mode]').forEach((row) => {
      const isActive = row.dataset.curMode === mode;
      row.hidden = !isActive;
      row.toggleAttribute('aria-hidden', !isActive);
      row.querySelectorAll('input, select, button').forEach((control) => {
        const isCmFraction = control.matches('[data-cur-fraction]') && this.unit === 'cm';
        control.disabled = !isActive || isCmFraction;
      });
    });

    if (this.step === 3 && !this.getActiveMeasurementKeys().includes(this.activeMeasure)) {
      this.setActiveMeasure(this.getActiveMeasurementKeys()[0]);
    }

    this.updateDynamicMeasurementText();
  }

  updateDynamicMeasurementText() {
    const measureStep = this.querySelector('[data-cur-step="3"]');
    const measureText = measureStep ? measureStep.querySelector('[data-cur-measure-text]') : null;
    if (measureStep && measureText) {
      const isInstalled = this.getHardwareMode() === 'installed';
      const nextText = isInstalled
        ? measureStep.dataset.installedText || ''
        : measureStep.dataset.notInstalledText || '';

      measureText.textContent = nextText;
      measureText.hidden = !nextText;
      measureStep.classList.toggle('ctm-cur_step--no-text', !nextText);
    }

    const hint = this.querySelector('[data-cur-dynamic-height-hint]');
    if (!hint) return;
    hint.textContent = this.getInstalledHeightHint(hint);
  }

  updateResults() {
    if (!this.resultWidth || !this.resultLength) return;

    const width = this.calculateWidth();
    const length = this.calculateLength();
    const displayWidth = this.getDisplayWidth(width);
    const panelNote = this.getPanelWidthNote(width);

    this.resultWidth.textContent = displayWidth ? `${this.formatMeasurement(displayWidth)}${panelNote}` : '--';
    this.resultLength.textContent = length ? this.formatMeasurement(length) : '--';
  }

  getDisplayWidth(width) {
    const panel = this.getSelectedOption('panel');
    if (!panel || !width) return width;
    if (this.isRippleFoldHeading()) return width;

    return panel.value === 'pair' ? width / 2 : width;
  }

  getPanelWidthNote(width) {
    const panel = this.getSelectedOption('panel');
    if (!panel || !width) return '';

    const panelMode = panel.value === 'pair' ? 'pair' : 'single';

    if (this.isRippleFoldHeading()) {
      const note = panelMode === 'pair'
        ? this.resultWidth?.dataset.rippleFoldPairWidthNote || ''
        : this.resultWidth?.dataset.rippleFoldSingleWidthNote || '';
      return note ? ` (${note})` : '';
    }

    const note = this.resultWidth?.dataset.panelWidthNote || '';
    return note ? ` (${note})` : '';
  }

  calculateWidth() {
    const fullnessMultiplier = this.getFullnessMultiplier();
    let width = 0;

    if (this.getHardwareMode() === 'installed') {
      width = this.getValueInCurrentUnit('rod_width');
      return width ? Math.max(0, width * fullnessMultiplier) : 0;
    }

    const frameWidth = this.getValueInCurrentUnit('frame_width');
    const leftExtension = this.getValueInCurrentUnit('left_extension');
    const rightExtension = this.getValueInCurrentUnit('right_extension');
    if (!frameWidth && !leftExtension && !rightExtension) return 0;
    width = frameWidth + leftExtension + rightExtension;
    return Math.max(0, width * fullnessMultiplier);
  }

  getFullnessMultiplier() {
    const fullness = this.getSelectedOption('fullness');
    if (!fullness || !this.needsFullnessStep()) return 1;

    const multiplier = Number(fullness.dataset.curFullnessMultiplier);
    return Number.isFinite(multiplier) && multiplier > 0 ? multiplier : 1;
  }

  calculateLength() {
    let baseLength = 0;

    if (this.getHardwareMode() === 'installed') {
      baseLength = this.getValueInCurrentUnit('hardware_to_floor');
      return baseLength ? Math.max(0, baseLength + this.getFinishedLookOffset()) : 0;
    }

    const floor = this.getValueInCurrentUnit('frame_to_floor');
    const mount = this.getValueInCurrentUnit('mounting_height');
    if (!floor && !mount) return 0;
    baseLength = floor + mount;
    return Math.max(0, baseLength + this.getFinishedLookOffset());
  }

  getFinishedLookOffset() {
    const finish = this.getSelectedOption('finish');
    const finishValue = finish ? finish.value : '';
    const offsetsInInches = {
      hover: -1,
      float: -1,
      kiss: 0,
      puddle: 1
    };
    const offsetInInches = offsetsInInches[finishValue] || 0;
    return this.unit === 'cm' ? offsetInInches * 2.54 : offsetInInches;
  }

  getValueInCurrentUnit(key) {
    const inches = this.getValueInInches(key);
    return this.unit === 'cm' ? inches * 2.54 : inches;
  }

  formatMeasurement(value) {
    if (this.unit === 'cm') return `${this.formatCmResultNumber(value)} cm`;

    const wholeAndFraction = this.toWholeAndFraction(value);
    return wholeAndFraction.fraction === '0'
      ? `${wholeAndFraction.number || '0'} Inches`
      : `${wholeAndFraction.number || '0'} ${wholeAndFraction.fraction} Inches`;
  }

  toWholeAndFraction(value) {
    let whole = Math.floor(Math.max(0, value));
    let fraction = this.decimalToFraction(value - whole);
    if (fraction === '1') {
      whole += 1;
      fraction = '0';
    }
    return { number: String(whole), fraction };
  }

  fractionToDecimal(fraction) {
    if (!fraction || fraction === '0') return 0;
    const parts = fraction.split('/');
    if (parts.length !== 2) return 0;
    const numerator = Number(parts[0]);
    const denominator = Number(parts[1]);
    if (!denominator) return 0;
    return numerator / denominator;
  }

  decimalToFraction(decimal) {
    const eighths = Math.round(decimal * 8);
    const map = {
      0: '0',
      1: '1/8',
      2: '1/4',
      3: '3/8',
      4: '1/2',
      5: '5/8',
      6: '3/4',
      7: '7/8',
      8: '1'
    };
    return map[eighths] || '0';
  }

  formatNumber(value, decimals) {
    const multiplier = 10 ** decimals;
    const rounded = Math.round((value + 1e-8) * multiplier) / multiplier;
    return rounded.toFixed(decimals);
  }

  formatCmResultNumber(value) {
    const rounded = Math.round((Number(value) + 1e-8) * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  }

  startOver() {
    this.querySelectorAll('[data-cur-option]').forEach((option) => {
      option.checked = false;
    });

    Object.keys(this.measurements).forEach((key) => {
      this.measurements[key].number = '';
      this.measurements[key].fraction = '0';
      this.measurements[key].sourceUnit = '';
      this.measurements[key].sourceNumber = '';
      this.measurements[key].sourceFraction = '0';
    });

    this.applyDefaultMeasurements();
    this.syncMeasurementControls();
    this.setActiveMeasure('frame_width');
    this.updateMeasurementMode();
    this.clearError();
    this.showStep(1);
  }

  getSelectedOption(type) {
    return this.querySelector(`[data-cur-option="${type}"]:checked`);
  }

  ensureDefaultSelectionForCurrentStep() {
    const optionTypesByStep = {
      1: 'hardware',
      2: 'heading',
      4: 'panel',
      5: 'fullness'
    };
    const optionType = optionTypesByStep[this.step];
    if (!optionType || this.getSelectedOption(optionType)) return false;

    const defaultOption = Array.from(this.querySelectorAll(`[data-cur-option="${optionType}"]`)).find((option) => {
      return !option.disabled && !option.closest('[hidden]');
    });

    if (!defaultOption) return false;

    defaultOption.checked = true;
    return true;
  }

  ensureDefaultFinishSelection() {
    if (this.step !== 6 || this.getSelectedOption('finish')) return;

    const finishOptions = Array.from(this.querySelectorAll('[data-cur-option="finish"]'));
    const kissOption = finishOptions.find((option) => this.normalizeOptionValue(option.value) === 'kiss');
    const defaultOption = kissOption || finishOptions[0];
    if (defaultOption) defaultOption.checked = true;
  }

  getHardwareMode() {
    const hardware = this.getSelectedOption('hardware');
    if (!hardware) return 'not-installed';
    const normalizedValue = this.normalizeOptionValue(hardware.value);
    return normalizedValue === 'installed' ? 'installed' : 'not-installed';
  }

  normalizeOptionValue(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[\s_]+/g, '-');
  }

  needsFullnessStep() {
    return this.getAllowedFullnessMultipliers().length > 0;
  }

  getSelectedHeadingValue() {
    const heading = this.getSelectedOption('heading');
    return heading ? heading.value : '';
  }

  getAllowedFullnessMultipliers() {
    const heading = this.getSelectedOption('heading');
    if (!heading) return [];

    return String(heading.dataset.curFullnessValues || '')
      .split(',')
      .map((value) => Number(value.trim()))
      .filter((value, index, values) => Number.isFinite(value) && value > 0 && values.indexOf(value) === index);
  }

  updateFullnessOptions() {
    const allowedMultipliers = this.getAllowedFullnessMultipliers();
    const shouldShowFullness = allowedMultipliers.length > 0;

    this.querySelectorAll('[data-cur-option="fullness"]').forEach((option) => {
      const multiplier = Number(option.dataset.curFullnessMultiplier);
      const isAllowed = shouldShowFullness && allowedMultipliers.includes(multiplier);
      const card = option.closest('.ctm-cur_card');

      if (card) {
        card.hidden = !isAllowed;
        card.toggleAttribute('aria-hidden', !isAllowed);
      }

      option.disabled = !isAllowed;
      if (!isAllowed && option.checked) option.checked = false;
    });
  }

  isRippleFoldHeading() {
    return this.normalizeOptionValue(this.getSelectedHeadingValue()) === 'ripple-fold';
  }

  getActiveMeasurementKeys() {
    return this.getHardwareMode() === 'installed'
      ? ['rod_width', 'hardware_to_floor']
      : ['frame_width', 'left_extension', 'right_extension', 'frame_to_floor', 'mounting_height'];
  }

  getInstalledHeightHint(hintElement) {
    const visualType = this.getInstalledHeightVisualType();
    if (visualType === 'track-bottom') return hintElement.dataset.trackBottomHint || 'Measure from the track bottom';
    if (visualType === 'rod-top') return hintElement.dataset.rodTopHint || 'Measure from the rod top';
    return hintElement.dataset.ringEyeletHint || 'Measure from the ring eyelet/track runner';
  }

  getInstalledHeightVisualType() {
    const headingValue = this.normalizeOptionValue(this.getSelectedHeadingValue());
    if (this.trackBottomHeightHeadings.includes(headingValue)) return 'track-bottom';
    if (this.rodTopHeightHeadings.includes(headingValue)) return 'rod-top';
    return 'ring-eyelet';
  }

  getOptionTitle(input) {
    const card = input.closest('.ctm-cur_card');
    const title = card ? card.querySelector('.ctm-cur_card-ttl') : null;
    return title ? title.textContent.trim() : input.value;
  }

  getMeasureLabel(row) {
    const label = row ? row.querySelector('.ctm-cur_lbl') : null;
    return label ? label.textContent.trim() : 'Current';
  }

  getCurrentStepElement() {
    return this.querySelector(`[data-cur-step="${this.step}"]`);
  }

  focusCurrentStep() {
    const activeStep = this.getCurrentStepElement();
    if (!activeStep) return;

    if (this.step === 3) {
      const firstMeasureInput = Array.from(activeStep.querySelectorAll('[data-cur-measure-input]')).find((input) => {
        return !input.disabled && !input.closest('[hidden]');
      });

      if (firstMeasureInput) {
        this.setActiveMeasure(firstMeasureInput.dataset.curMeasureInput);
        this.focusElement(firstMeasureInput);

        requestAnimationFrame(() => {
          this.focusElement(firstMeasureInput);

          window.setTimeout(() => {
            if (document.activeElement !== firstMeasureInput) this.focusElement(firstMeasureInput);
          }, 60);
        });
        return;
      }
    }

    const target = activeStep.querySelector('[data-cur-step-title]') || activeStep.querySelector('input, button, select, a');
    if (target) this.focusElement(target);
  }

  focusElement(element) {
    if (!element) return;

    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      element.focus();
    }
  }

  showError(message) {
    if (this.error) this.error.textContent = message;
    this.announce(message);
  }

  clearError() {
    if (this.error) this.error.textContent = '';
  }

  announce(message) {
    if (!this.liveRegion) return;
    this.liveRegion.textContent = '';
    window.setTimeout(() => {
      this.liveRegion.textContent = message;
    }, 20);
  }
}

if (!customElements.get('custom-measure-curtain')) {
  customElements.define('custom-measure-curtain', CustomMeasureCurtain);
}
